import { useState } from "react";
import { KeyRound } from "lucide-react";
import { useEmitirChave } from "@/api/consultas";
import { Botao } from "@/componentes/Botao";
import { Confirmacao } from "@/componentes/Confirmacao";
import { Dica } from "@/componentes/Dica";
import { useSessao } from "@/sessao/useSessao";
import { ChaveEmitida } from "./ChaveEmitida";
import {
  efeitoDaChave,
  ESCOPOS_DA_TELA,
  ESCOPOS_INICIAIS,
  naOrdemDoCatalogo,
  severidadeDaChave,
  type AmbienteDaChave,
  type EscopoDaTela,
} from "./escopos";

interface PedidoDeChave {
  ambiente: AmbienteDaChave;
  escopos: readonly EscopoDaTela[];
}

interface ChaveNova {
  chave: string;
  prefixo: string;
  ambiente: string;
  escopos: string[];
}

/*
 * Emissão de chave, na própria aba: os escopos em linha, e cada ambiente é um botão.
 *
 * Fica na aba, e não em modal, porque o modal de cadastro não desabilita o envio, e a spec
 * pede o botão travado sem escopo. A confirmação vem depois, e é ela que acompanha o poder da
 * chave, a partir do pedido e nunca do texto do título: o nome da aplicação está no título,
 * e "Clínica live" faria a chave `test` sair como `live`.
 *
 * Escopos são obrigatórios e validados: herdá-los em silêncio, ou assumir um conjunto amplo, é
 * como uma chave de leitura vira chave de escrita sem ninguém decidir. Por isso a marcação
 * volta ao início depois de emitir, e `cadastro:write` não sobra para a chave seguinte.
 *
 * A credencial fica só no estado deste componente, para o `ChaveEmitida`: não vai para log,
 * cache de consulta nem URL.
 */
export function EmitirChave({
  sistemaID,
  sistemaNome,
}: {
  sistemaID: string;
  sistemaNome: string;
}) {
  const { cliente } = useSessao();
  const emitir = useEmitirChave(cliente, sistemaID);

  const [marcados, setMarcados] = useState<readonly EscopoDaTela[]>(ESCOPOS_INICIAIS);
  const [pedido, setPedido] = useState<PedidoDeChave | null>(null);
  const [chaveNova, setChaveNova] = useState<ChaveNova | null>(null);

  function alternar(escopo: EscopoDaTela, marcado: boolean) {
    setMarcados((atuais) =>
      marcado ? [...atuais, escopo] : atuais.filter((atual) => atual !== escopo),
    );
  }

  const severidade = pedido && severidadeDaChave(pedido.ambiente, pedido.escopos);

  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <fieldset className="flex flex-col gap-1">
        <legend className="rotulo">Escopos</legend>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {ESCOPOS_DA_TELA.map(({ escopo, dica }) => (
            <span key={escopo} className="flex items-center gap-1">
              <label className="dados flex items-center gap-2 text-corpo text-texto">
                <input
                  type="checkbox"
                  checked={marcados.includes(escopo)}
                  onChange={(evento) => alternar(escopo, evento.target.checked)}
                />
                {escopo}
              </label>
              <Dica texto={dica} />
            </span>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-wrap gap-2">
        {(["test", "live"] as const).map((ambiente) => (
          <Botao
            key={ambiente}
            tom={ambiente === "live" ? "primario" : "secundario"}
            disabled={marcados.length === 0}
            onClick={() => setPedido({ ambiente, escopos: naOrdemDoCatalogo(marcados) })}
          >
            <KeyRound aria-hidden="true" className="size-4" />
            Emitir chave {ambiente}
          </Botao>
        ))}
      </div>

      <Confirmacao
        pedido={
          pedido === null || severidade === null
            ? null
            : {
                severidade,
                titulo: `Emitir chave ${pedido.ambiente} para ${sistemaNome}?`,
                corpo: (
                  <>
                    <p>{efeitoDaChave(pedido.ambiente, pedido.escopos)}</p>
                    <p className="dados mt-2 text-[12px] text-texto">{pedido.escopos.join(", ")}</p>
                  </>
                ),
                verbo: "Emitir chave",
                ...(severidade === "critica" ? { digitarParaLiberar: sistemaNome } : {}),
              }
        }
        aoCancelar={() => setPedido(null)}
        aoConfirmar={async () => {
          if (pedido === null) return;

          const emitida = await emitir.mutateAsync({
            ambiente: pedido.ambiente,
            escopos: [...pedido.escopos],
          });
          setPedido(null);
          setMarcados(ESCOPOS_INICIAIS);
          setChaveNova({
            chave: emitida.chave,
            prefixo: emitida.prefixo,
            ambiente: emitida.ambiente,
            escopos: emitida.escopos,
          });
        }}
      />

      {chaveNova !== null && (
        <ChaveEmitida
          chave={chaveNova.chave}
          prefixo={chaveNova.prefixo}
          ambiente={chaveNova.ambiente}
          escopos={chaveNova.escopos}
          aoFechar={() => setChaveNova(null)}
        />
      )}
    </div>
  );
}
